import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { createTheme } from '@mui/material';
import React from 'react';
import { isPrintToPdfMode } from '../utils';
import { deprecatedColors } from './colors/deprecatedColors';
import { themeColorsDark } from './colors/themeColorsDark';
import { themeColorsLight } from './colors/themeColorsLight';
import { typography } from './typography';
/**
 * @deprecated Use `themeColors`.
 */
export const juvareColors = deprecatedColors;
/**
 * Theme mode. User can change this.
 */
export const themeMode = isPrintToPdfMode() ? 'light' : getThemeMode();
function getThemeMode() {
    const defaultThemeMode = window.juvareUserDefaultThemeMode;
    const preferredThemeMode = window.localStorage.getItem('juvareUserPreferredThemeMode');
    if (preferredThemeMode === 'light' || preferredThemeMode === 'dark') {
        return preferredThemeMode;
    }
    else if (defaultThemeMode === 'light' || defaultThemeMode === 'dark') {
        return defaultThemeMode;
    }
    else {
        return 'light';
    }
}
/**
 * Sets or removes the user's preferred theme mode in local storage.
 *
 * @param {'light' | 'dark'} [mode] - The theme mode to set. If omitted, the preference is removed.
 */
export function setPreferredThemeMode(mode) {
    if (mode) {
        window.localStorage.setItem('juvareUserPreferredThemeMode', mode);
    }
    else {
        window.localStorage.removeItem('juvareUserPreferredThemeMode');
    }
}
export const themeColors = themeMode === 'dark' ? themeColorsDark : themeColorsLight;
/**
 * Creates a custom Juvare MUI theme with preconfigured component overrides and palette values.
 *
 * @param {function(ThemeOptions): ThemeOptions} [customizeOptions] - Optional function to modify the base theme options.
 * @param {...object} args - Additional localization or customization objects to be merged into the theme (e.g., MUI locales).
 * @returns {Theme} The configured MUI theme object.
 */
export function createJuvareTheme(customizeOptions, ...args) {
    let options = {
        components: {
            MuiTypography: {
                defaultProps: {
                    variantMapping: {
                        subtitle1: 'p',
                        subtitle2: 'p',
                        d1: 'h1',
                        d2: 'h2',
                        d3: 'h3',
                    },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    colorPrimary: {
                        '&:hover': { backgroundColor: themeColors.accent.blue.alpha },
                        // MuiTouchRipple is going to be removed from the public API in material v6, so can't override styles there
                        '.MuiTouchRipple-root': {
                            '.MuiTouchRipple-rippleVisible': {
                                opacity: 1,
                                '.MuiTouchRipple-child': {
                                    backgroundColor: themeColors.accent.blue.alpha,
                                },
                            },
                        },
                        '&.Mui-checked': {
                            '&:hover': { backgroundColor: themeColors.accent.blue.alpha },
                        },
                    },
                    thumb: {
                        '.Mui-checked & ': {
                            color: themeColors.icon.link,
                        },
                        '.Mui-disabled & ': {
                            color: themeColors.overlay.disable,
                        },
                    },
                    track: {
                        backgroundColor: themeColors.overlay.inverse,
                        opacity: 0.32,
                        '.Mui-checked.Mui-checked + &': {
                            backgroundColor: themeColors.primary.border,
                            opacity: 0.38,
                        },
                        '.Mui-disabled.Mui-disabled + &': {
                            backgroundColor: themeColors.overlay.inverse,
                            opacity: 0.32,
                        },
                    },
                },
            },
            MuiLink: {
                defaultProps: {
                    underline: 'hover',
                    color: themeColors.text.link,
                },
            },
            MuiButtonBase: {
                styleOverrides: {
                    root: () => ({
                        'header &.Mui-focusVisible:not(.MuiButton-root),header &:focus-visible:not(.MuiButton-root)': {
                            backgroundColor: themeColorsLight.alphaLightSurface.base,
                            outline: `2px solid ${themeColors.primary.base}`,
                            outlineOffset: '-1px',
                            '& .MuiTouchRipple-root': {
                                display: 'none',
                            },
                        },
                        'nav &.Mui-focusVisible:not(.MuiButton-root),nav &:focus-visible:not(.MuiButton-root)': {
                            '& .MuiTouchRipple-root': {
                                display: 'none',
                            },
                        },
                        'nav &.Mui-focusVisible:not(.MuiButton-root) .MuiBox-root,nav &:focus-visible:not(.MuiButton-root) .MuiBox-root': {
                            backgroundColor: themeColors.accent.blue.alpha,
                            outline: `2px solid ${themeColors.primary.base}`,
                        },
                    }),
                },
            },
            MuiTab: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: () => ({
                        '&.Mui-focusVisible:not(.MuiButton-root),&:focus-visible:not(.MuiButton-root)': {
                            backgroundColor: themeColors.accent.blue.alpha,
                            outline: `2px solid ${themeColors.primary.base}`,
                            outlineOffset: '-2px',
                        },
                    }),
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: ({ ownerState }) => (Object.assign(Object.assign(Object.assign(Object.assign({}, (ownerState.color === 'primary' &&
                        ownerState.variant === 'outlined' && {
                        '&.Mui-focusVisible, &:focus-visible': {
                            backgroundColor: themeColors.accent.blue.alpha,
                            outline: `2px solid ${themeColors.primary.border}`,
                            outlineOffset: '-2px',
                        },
                        '& .MuiTouchRipple-root': {
                            display: 'none',
                        },
                    })), (ownerState.color === 'primary' &&
                        ownerState.variant === 'contained' && {
                        '&.Mui-focusVisible, &:focus-visible': {
                            backgroundColor: themeColors.primary.hover,
                            boxShadow: `inset 0 0 0 3px ${themeColors.icon.inverse}`,
                            outline: `2px solid ${themeColors.primary.border}`,
                            outlineOffset: '-2px',
                        },
                        '& .MuiTouchRipple-root': {
                            display: 'none',
                        },
                    })), (ownerState.color === 'primary' &&
                        ownerState.variant === 'text' && {
                        '&.Mui-focusVisible, &:focus-visible': {
                            backgroundColor: themeColors.accent.blue.alpha,
                            outline: `2px solid ${themeColors.primary.border}`,
                            outlineOffset: '-2px',
                        },
                        '& .MuiTouchRipple-root': {
                            display: 'none',
                        },
                    })), (ownerState.color === 'inherit' &&
                        ownerState.variant === 'text' && {
                        '&.Mui-focusVisible, &:focus-visible': {
                            backgroundColor: themeColors.alphaLightSurface.base,
                            color: themeColors.decorative.white,
                            outline: `2px solid ${themeColors.decorative.white}`,
                            outlineOffset: '-2px',
                        },
                        '& .MuiTouchRipple-root': {
                            display: 'none',
                        },
                    }))),
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    determinate: {
                        ['.MuiLinearProgress-barColorPrimary']: { backgroundColor: themeColors.accent.green.base },
                        backgroundColor: themeColors.accent.green.subtlest,
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    colorPrimary: {
                        color: themeColors.primary.base,
                    },
                },
            },
            MuiAlert: {
                defaultProps: {
                    iconMapping: {
                        warning: React.createElement(ErrorOutlineIcon, null),
                        success: React.createElement(CheckCircleOutlineIcon, null),
                    },
                },
                styleOverrides: {
                    message: {
                        fontWeight: 500,
                    },
                    standardSuccess: {
                        color: themeColors.accent.green.bold,
                        backgroundColor: themeColors.accent.green.subtlest,
                        ['.MuiAlert-icon']: { color: 'inherit' },
                    },
                    standardInfo: {
                        color: themeColors.accent.blue.bold,
                        backgroundColor: themeColors.accent.blue.subtlest,
                        ['.MuiAlert-icon']: { color: 'inherit' },
                    },
                    standardWarning: {
                        color: themeColors.accent.orange.bold,
                        backgroundColor: themeColors.accent.orange.subtlest,
                        ['.MuiAlert-icon']: { color: 'inherit' },
                    },
                    standardError: {
                        color: themeColors.accent.red.bold,
                        backgroundColor: themeColors.accent.red.subtlest,
                        ['.MuiAlert-icon']: { color: 'inherit' },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    colorPrimary: {
                        color: themeColors.primary.base,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: `2px solid ${themeColors.primary.base}`,
                        },
                    },
                },
            },
            // BEGIN differences from JuvareTheme
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        '&.MuiOutlinedInput-root': {
                            backgroundColor: themeColors.surface.primary,
                            '&.Mui-disabled': {
                                backgroundColor: themeColors.surface.disable,
                            },
                            '&:hover:not(.Mui-focused,.Mui-disabled,.Mui-error) .MuiOutlinedInput-notchedOutline': {
                                borderColor: themeColors.primary.border,
                            },
                        },
                    },
                    input: {
                        '&.MuiOutlinedInput-input': {
                            fontSize: 14, // changes TextField/Select input font size, was 16px, need to adjust a bunch of other values too
                            height: '1.25em', // such as this one, was 1.4375em
                            paddingTop: '19.25px', // and this one, was: 16.5px
                            paddingBottom: '19.25px', // and this one, was: 16.5px
                            '&.MuiInputBase-inputMultiline': {
                                paddingTop: '0px', // and this one
                                paddingBottom: '0px', // and this one
                            },
                            '&.MuiSelect-select': {
                                minHeight: '1.25em', // and this one, was 1.4375em
                                paddingTop: '17.25px', // and this one, was: 16.5px
                                paddingBottom: '21.25px', // and this one, was: 16.5px
                            },
                            '&.MuiInputBase-inputSizeSmall': {
                                paddingTop: '11.25px', // and this one, was: 8.5px
                                paddingBottom: '11.25px', // and this one, was: 8.5px
                                '&.MuiInputBase-inputMultiline': {
                                    paddingTop: '0px', // and this one
                                    paddingBottom: '0px', // and this one
                                },
                                '&.MuiSelect-select': {
                                    paddingTop: '9.25px', // and this one, was: 8.5px
                                    paddingBottom: '13.25px', // and this one, was: 8.5px
                                },
                            },
                        },
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    input: {
                        '&.MuiOutlinedInput-input': {
                            paddingTop: '10.75px', // and this one, was: 7.5px
                            paddingBottom: '9.75px', // and this one, was: 7.5px
                            '&.MuiInputBase-inputSizeSmall': {
                                paddingTop: '5.5px', // and this one, was: 2.5px
                                paddingBottom: '4.5px', // and this one, was: 2.5px
                            },
                        },
                    },
                    listbox: {
                        '&>.MuiAutocomplete-option': {
                            fontSize: 14, // changes checkbox label font size inside autocomplete dropdown, was 14px
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        '&.MuiInputLabel-outlined': {
                            fontSize: 14, // changes TextField/Select label font size, was 16px, need to adjust a bunch of other values too
                            transform: 'translate(14px, 18px) scale(1)', // such as this one, was: translate(14px, 16px) scale(1)
                            '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -9px) scale(0.857)', // and this one, was: translate(14px, -9px) scale(0.75)
                            },
                            '&.MuiInputLabel-sizeSmall': {
                                transform: 'translate(14px, 10px) scale(1)', // and this one, was: transform: translate(14px, 9px) scale(1);
                                '&.MuiInputLabel-shrink': {
                                    transform: 'translate(14px, -9px) scale(0.857)', // and this one, was: translate(14px, -9px) scale(0.75)
                                },
                            },
                        },
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        '>.MuiTypography-root': {
                            fontSize: 14, // changes Checkbox/Radio label font size, was 16px
                        },
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        '&:not(.MuiInputLabel-root)': {
                            // if not TextField/Select
                            fontSize: 14, // changes default TextField and any form control label
                            fontWeight: 500, // apply subtitle2 style
                            color: themeColors.text.bold, // makes it black instead of gray
                            '&.Mui-focused': {
                                color: themeColors.text.bold, // when focuses color does not change
                            },
                        },
                    },
                },
            },
            // END differences from JuvareTheme
        },
        palette: {
            mode: themeMode,
            primary: {
                main: themeColors.primary.base,
                light: themeColors.primary.border, // TODO ask UX, default #42a5f5
                dark: themeColors.primary.pressed, // TODO ask UX, default #1565c0
            },
            secondary: {
                main: themeColors.secondary.base,
                light: themeColors.secondary.border, // TODO ask UX, default #ba68c8
                dark: themeColors.secondary.pressed, // TODO ask UX, default #7b1fa2
            },
            // TODO BEGIN ask UX, currently default values are set
            divider: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            action: {
                active: themeMode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.54)',
                hover: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                hoverOpacity: themeMode === 'dark' ? 0.08 : 0.04,
                selected: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)',
                selectedOpacity: themeMode === 'dark' ? 0.16 : 0.08,
                disabled: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                disabledBackground: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                disabledOpacity: themeMode === 'dark' ? 0.38 : 0.38,
                focus: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                focusOpacity: themeMode === 'dark' ? 0.12 : 0.12,
                activatedOpacity: themeMode === 'dark' ? 0.24 : 0.12,
            },
            // END ask UX
            background: {
                default: themeColors.background,
            },
            text: {
                primary: themeColors.text.main,
                secondary: themeColors.text.subtle,
                disabled: themeColors.text.disable,
            },
            error: {
                main: themeColors.secondary.base,
                light: themeColors.accent.red.subtle,
                dark: themeColors.accent.red.bold,
            },
            warning: {
                main: themeColors.accent.orange.base,
                light: themeColors.accent.orange.subtle,
                dark: themeColors.accent.orange.bold,
            },
            info: {
                main: themeColors.accent.blue.base,
                light: themeColors.accent.blue.subtle,
                dark: themeColors.accent.blue.bold,
            },
            success: {
                main: themeColors.accent.green.base,
                light: themeColors.accent.green.subtle,
                dark: themeColors.accent.green.bold,
            },
        },
        typography,
    };
    if (customizeOptions) {
        options = customizeOptions(options);
    }
    return createTheme(options, ...args);
}
/**
 * @deprecated This theme is deprecated and should not be used in new code.
 * Use the updated theming approach provided by `createJuvareTheme` instead.
 *
 * @returns  {Theme} A Material UI `Theme` object with dark mode settings.
 */
export function createJuvareAppMenuTheme() {
    return createTheme({
        palette: {
            mode: 'dark',
        },
    });
}
